import { useCallback, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { LayoutStorageState, useLayoutStorage } from '@/stores/layout.ts';

type GenericConfigProps<T> = {
  configInput?: T;
  defaultConfig: T;
  widgetId: string;
};

type GenericPropsType<T> = {
  config: T;
  update: ({ widgetId, config }: { widgetId: string; config: T }) => void;
  remove: ({ widgetId }: { widgetId: string }) => void;
  size: { w: number; h: number } | undefined;
};

export const useLayoutProps = <T>({
  configInput,
  defaultConfig,
  widgetId,
}: GenericConfigProps<T>): GenericPropsType<T> => {
  const widgetSizeGetter = useCallback(
    (state: LayoutStorageState) => {
      const layout = state.storageLayouts.find(layout =>
        layout.widgets.some(widget => widget.id === widgetId)
      );

      const widget = layout?.widgets.find(widget => widget.id === widgetId);

      return widget
        ? {
            w: widget.w,
            h: widget.h,
          }
        : undefined;
    },
    [widgetId]
  );

  const update = useLayoutStorage(s => s.updateWidgetConfigProps);
  const remove = useLayoutStorage(s => s.removeWidgetConfigPropsById);
  const size = useLayoutStorage(useShallow(s => widgetSizeGetter(s)));

  // Subscribe to this widget's config so we re-render when it changes (e.g. after Apply filter).
  const storeConfig = useLayoutStorage(
    useShallow(s => s.getWidgetConfigProps(widgetId) as T | undefined)
  );

  const config = useMemo(
    () => configInput ?? storeConfig ?? defaultConfig,
    [configInput, storeConfig, defaultConfig]
  );

  return {
    config,
    update,
    remove,
    size,
  };
};
